import { useEffect, useRef, useState } from "react";
import { Menu, X, ArrowUpRight, Globe, Check } from "lucide-react";
import Logo from "./Logo";
import { useI18n } from "@/i18n";

const NAV_IDS = ["home", "about", "products", "brands", "why", "process", "contact"];
const LANGS = [
  { code: "en", label: "English", short: "EN" },
  { code: "ar", label: "العربية", short: "AR" },
  { code: "es", label: "Español", short: "ES" },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function Header() {
  const { t, locale, setLocale } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    setTimeout(() => scrollToId(id), 50);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/60 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-[72px]">
          {/* Brand */}
          <button
            data-testid="header-brand"
            onClick={() => handleNav("home")}
            className="flex items-center gap-3 group"
          >
            <span className="relative inline-flex items-center justify-center w-11 h-11 rounded-xl glass">
              <Logo size={28} />
            </span>
            <span className="font-display text-[15px] md:text-[16px] font-semibold tracking-tight text-white">
              {t("brand.name_prefix")}
              <span className="text-[#A8A8B3] font-medium"> {t("brand.name_suffix")}</span>
            </span>
          </button>

          {/* Nav */}
          <nav className="hidden lg:flex items-center gap-1 glass rounded-full px-2 py-1.5">
            {NAV_IDS.map((id) => (
              <button
                key={id}
                data-testid={`nav-${id}`}
                onClick={() => handleNav(id)}
                className="px-3.5 py-2 text-[13.5px] text-[#C9CAD1] hover:text-white rounded-full hover:bg-white/5 transition-colors"
              >
                {t(`nav.${id}`)}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <LanguageMenu locale={locale} setLocale={setLocale} />
            <button
              data-testid="header-cta-quote"
              onClick={() => handleNav("contact")}
              className="btn-primary text-sm"
            >
              {t("common.request_quote")}
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile controls */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageMenu locale={locale} setLocale={setLocale} />
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="w-10 h-10 rounded-xl glass flex items-center justify-center text-white"
              aria-label="Toggle menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="mx-5 mb-4 glass-strong rounded-2xl p-3">
          <ul className="flex flex-col">
            {NAV_IDS.map((id) => (
              <li key={id}>
                <button
                  data-testid={`mobile-nav-${id}`}
                  onClick={() => handleNav(id)}
                  className="w-full text-start px-4 py-3 text-[15px] text-[#D5D6DC] hover:text-white hover:bg-white/5 rounded-xl"
                >
                  {t(`nav.${id}`)}
                </button>
              </li>
            ))}
            <li className="pt-2">
              <button
                data-testid="mobile-cta-quote"
                onClick={() => handleNav("contact")}
                className="btn-primary w-full justify-center text-sm"
              >
                {t("common.request_quote")} <ArrowUpRight className="w-4 h-4" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
}

function LanguageMenu({ locale, setLocale }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => l.code === locale) || LANGS[0];

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="lang-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="glass rounded-full h-10 px-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-white/90 hover:text-white hover:bg-white/5 transition"
      >
        <Globe className="w-3.5 h-3.5 text-[#8B6DFF]" />
        <span className="text-white">{current.short}</span>
      </button>

      <div
        className={`absolute end-0 mt-2 min-w-[150px] glass-strong rounded-2xl p-1.5 z-50 origin-top transition-all duration-200 ${
          open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
        }`}
        role="menu"
      >
        {LANGS.map((l) => {
          const isActive = l.code === locale;
          return (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => {
                setLocale(l.code);
                setOpen(false);
              }}
              role="menuitemradio"
              aria-checked={isActive}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-xl text-[13px] transition-colors ${
                isActive
                  ? "bg-white/10 text-white"
                  : "text-[#C9CAD1] hover:text-white hover:bg-white/5"
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="text-[10px] tracking-[0.18em] text-[#8B6DFF] w-5">
                  {l.short}
                </span>
                <span>{l.label}</span>
              </span>
              {isActive ? <Check className="w-3.5 h-3.5 text-[#8B6DFF]" /> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
